import React, { useState } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/ui/ConfirmModal';
import PanelCard from '../../components/ui/PanelCard';
import UserPicker from '../../components/ui/UserPicker';

const PLUS_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
       strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v8M8 12h8" />
  </svg>
);

/** N nap jóváírása EGY kiválasztott felhasználónak. */
function SingleUserExtend() {
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [days, setDays] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const dayCount = parseInt(days, 10) || 0;

  const submit = async () => {
    setPending(false);
    setLoading(true);
    setLastResult(null);
    try {
      const { data } = await api.post('/owner/extend-subscription', {
        userId: user.id, days: dayCount,
      });
      setLastResult(data);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not extend the subscription.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PanelCard
        title="Give days to one user"
        subtitle="For a single account — compensation, support cases, or a gift."
        icon={PLUS_ICON}
      >
        <div className="space-y-4">
          <div>
            <label className="label">User</label>
            <UserPicker value={user} onChange={u => { setUser(u); setLastResult(null); }} />
          </div>

          <div>
            <label className="label">Days to add</label>
            <input
              type="number" min="1" max="3650" className="input"
              value={days}
              onChange={e => setDays(e.target.value)}
            />
          </div>

          <button
            onClick={() => setPending(true)}
            disabled={loading || !user || dayCount < 1}
            className="btn-primary w-full py-2.5"
          >
            {loading
              ? 'Adding…'
              : user
                ? `Add ${dayCount} day(s) to ${user.username}`
                : 'Pick a user first'}
          </button>

          {lastResult && (
            <div className={`p-4 rounded-xl border ${
              lastResult.mode === 'revived'
                ? 'bg-amber-500/[0.08] border-amber-500/20'
                : 'bg-emerald-500/[0.08] border-emerald-500/20'
            }`}>
              <p className={`text-sm font-medium ${
                lastResult.mode === 'revived' ? 'text-amber-300' : 'text-emerald-300'
              }`}>
                {lastResult.message}
              </p>
              {lastResult.mode === 'frozen' ? (
                <p className="text-xs text-gray-500 mt-1">
                  The subscription is frozen, so the time was added to its stored remaining
                  time — it starts counting again when you unfreeze it.
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  New expiry: {lastResult.expiresAt?.slice(0, 16).replace('T', ' ')} UTC
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-gray-600 leading-relaxed">
            A frozen subscription gets the time added to its remaining time. An
            <strong className="text-gray-500"> already expired</strong> one is revived
            from now — adding to its old date would land in the past and change nothing.
            An account with no subscription at all cannot be extended; generate a key instead.
          </p>
        </div>
      </PanelCard>

      {pending && user && (
        <ConfirmModal
          title={`Add ${dayCount} day(s) to ${user.username}?`}
          message={`Only this account is affected. ${user.email}`}
          confirmLabel="Add days"
          onConfirm={submit}
          onCancel={() => setPending(false)}
        />
      )}
    </>
  );
}

/** N nap jóváírása MINDEN aktív és fagyasztott előfizetésnek. */
function GlobalExtend() {
  const { toast } = useToast();
  const [days, setDays] = useState(1);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [pending, setPending] = useState(false);

  const handleConfirm = async () => {
    setPending(false);
    setLoading(true);
    setLastResult(null);
    try {
      const res = await api.post('/owner/extend-subscriptions', { days: parseInt(days, 10) });
      setLastResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error extending subscriptions.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PanelCard
        title="Extend everyone"
        subtitle="Adds days to every currently active and frozen subscription at once."
        icon={PLUS_ICON}
      >
        <div className="space-y-4">
          <div>
            <label className="label">Days to add</label>
            <input
              type="number" min="1" max="3650" className="input"
              value={days}
              onChange={e => setDays(e.target.value)}
            />
            <p className="text-xs text-gray-600 mt-2">Between 1 and 3650 days.</p>
          </div>

          <button
            onClick={() => setPending(true)}
            disabled={loading || !days || parseInt(days, 10) < 1}
            className="btn-primary w-full py-2.5"
          >
            {loading
              ? 'Extending…'
              : `Add ${parseInt(days, 10) || 0} day(s) to all active subscribers`}
          </button>

          {lastResult && (
            <div className="p-4 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/20">
              <p className="text-sm text-emerald-300 font-medium">{lastResult.message}</p>
              <p className="text-xs text-gray-500 mt-1">
                {lastResult.affected} subscription(s) updated.
              </p>
            </div>
          )}

          <p className="text-xs text-gray-600 leading-relaxed">
            Already expired subscriptions are skipped — a mass action should not silently
            revive accounts that lapsed weeks ago. Use the single-user card for that.
          </p>
        </div>
      </PanelCard>

      {pending && (
        <ConfirmModal
          title="Extend all active subscriptions?"
          message={`This will add ${parseInt(days, 10)} day(s) to every active and frozen subscription. This cannot be undone.`}
          confirmLabel="Confirm"
          danger
          onConfirm={handleConfirm}
          onCancel={() => setPending(false)}
        />
      )}
    </>
  );
}

export default function SubscriptionsTab() {
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <SingleUserExtend />
      <GlobalExtend />
    </div>
  );
}
